import { useState, useEffect, useCallback } from "react";
import { router, usePage } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import {
  ChatbotPreviewPanel,
  type ChatbotPreviewData,
} from "@/components/ChatbotPreviewPanel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Clipboard,
  MessageCircle,
  Plus,
  Scissors,
  Smartphone,
  Sparkles,
  PartyPopper,
} from "lucide-react";
import type { Onboarding, Billing, AvailabilityRule, Business, PageProps } from "@/types";
import { HoursDayList } from "@/components/HoursDayList";

// --- Types ---

interface OnboardingData {
  services: {
    id: number;
    name: string;
    duration_minutes: number;
    price_cents: number;
    emoji: string | null;
  }[];
  staff: {
    id: number;
    name: string;
    pending: boolean;
    color: string | null;
  }[];
  availability: AvailabilityRule[];
  kapso_whatsapp: { status: string; display_address: string | null } | null;
  chatbot_preview: ChatbotPreviewData;
  has_outbound_messages: boolean;
}

interface OnboardingWizardProps {
  onboarding: Onboarding;
  onboardingData: OnboardingData;
  billing: Billing | null;
  orgSlug: string;
  currentBusiness: Business | null;
}

// --- Constants ---

const STEPS = [
  { id: "services", title: "Agregar servicios", icon: Scissors },
  { id: "demo_conversation", title: "Prueba tu agente", icon: MessageCircle },
  { id: "whatsapp", title: "Conectar WhatsApp", icon: Smartphone },
] as const;

const DEMO_PREREQUISITES = ["services"];

const DURATION_OPTIONS = [
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 45, label: "45 min" },
  { value: 60, label: "1 hora" },
  { value: 90, label: "1h 30min" },
  { value: 120, label: "2 horas" },
];

// --- Main Component ---

export function OnboardingWizard({
  onboarding,
  onboardingData,
  billing,
  orgSlug,
  currentBusiness,
}: OnboardingWizardProps) {
  const completedSteps = onboarding.completed_steps || [];
  const firstIncomplete = STEPS.find((s) => !completedSteps.includes(s.id));
  const [activeStep, setActiveStep] = useState<string>(
    firstIncomplete?.id || STEPS[0].id,
  );

  const completedCount = STEPS.filter((s) =>
    completedSteps.includes(s.id),
  ).length;
  const progressPercent = Math.round((completedCount / STEPS.length) * 100);

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
          <Sparkles className="h-4 w-4" />
          Configura tu agente de WhatsApp
        </div>
        <h1 className="text-2xl font-semibold text-foreground">
          ¡Bienvenido a {currentBusiness?.name || "Tippy"}!
        </h1>
        <p className="text-muted-foreground">
          Completa estos pasos para probar y activar tu agente de IA en WhatsApp
        </p>
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {completedCount} de {STEPS.length} pasos completados
          </span>
          <span className="font-medium text-foreground">
            {progressPercent}%
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {STEPS.map((step, index) => {
          const isComplete = completedSteps.includes(step.id);
          const isActive = activeStep === step.id;
          const Icon = step.icon;

          return (
            <div
              key={step.id}
              className="rounded-xl border border-border bg-card overflow-hidden"
            >
              {/* Step header */}
              <button
                type="button"
                onClick={() => setActiveStep(isActive ? "" : step.id)}
                className="flex w-full items-center gap-4 p-4 text-left hover:bg-accent/50 transition-colors"
              >
                <div
                  className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
                    isComplete
                      ? "bg-green-500 text-white"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {isComplete ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span
                      className={`font-medium ${isComplete ? "text-muted-foreground" : "text-foreground"}`}
                    >
                      {step.title}
                    </span>
                  </div>
                </div>
                {isActive ? (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                )}
              </button>

              {/* Step content */}
              {isActive && (
                <div className="border-t border-border p-4">
                  <StepContent
                    stepId={step.id}
                    onboardingData={onboardingData}
                    billing={billing}
                    orgSlug={orgSlug}
                    isComplete={isComplete}
                    completedSteps={completedSteps}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dismiss link */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            router.post(`/${orgSlug}/onboarding/dismiss`, undefined, {
              preserveScroll: true,
            });
          }}
          className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
        >
          Saltar configuración e ir al dashboard →
        </button>
      </div>
    </div>
  );
}

// --- Step Content Router ---

function StepContent({
  stepId,
  onboardingData,
  billing,
  orgSlug,
  isComplete,
  completedSteps,
}: {
  stepId: string;
  onboardingData: OnboardingData;
  billing: Billing | null;
  orgSlug: string;
  isComplete: boolean;
  completedSteps: string[];
}) {
  switch (stepId) {
    case "business_info":
      return (
        <BusinessInfoStep
          orgSlug={orgSlug}
          isComplete={isComplete}
        />
      );
    case "services":
      return (
        <ServicesStep
          services={onboardingData.services}
          orgSlug={orgSlug}
          isComplete={isComplete}
        />
      );
    case "staff":
      return (
        <StaffStep
          staff={onboardingData.staff}
          orgSlug={orgSlug}
          isComplete={isComplete}
        />
      );
    case "hours":
      return (
        <HoursStep
          availability={onboardingData.availability}
          orgSlug={orgSlug}
          isComplete={isComplete}
        />
      );
    case "demo_conversation":
      return (
        <DemoConversationStep
          preview={onboardingData.chatbot_preview}
          completedSteps={completedSteps}
          orgSlug={orgSlug}
          isComplete={isComplete}
        />
      );
    case "whatsapp":
      return (
        <WhatsAppStep
          kapsoWhatsapp={onboardingData.kapso_whatsapp}
          billing={billing}
          orgSlug={orgSlug}
          isComplete={isComplete}
          hasOutbound={onboardingData.has_outbound_messages}
        />
      );
    case "test_message":
      return (
        <TestMessageStep
          kapsoWhatsapp={onboardingData.kapso_whatsapp}
          hasOutbound={onboardingData.has_outbound_messages}
          orgSlug={orgSlug}
          isComplete={isComplete}
        />
      );
    default:
      return null;
  }
}

// --- Services Step ---

function BusinessInfoStep({
  orgSlug,
  isComplete,
}: {
  orgSlug: string;
  isComplete: boolean;
}) {
  const { current_business } = usePage<PageProps>().props;
  const [address, setAddress] = useState(current_business?.address || "");
  const [saving, setSaving] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    router.put(
      `/${orgSlug}/settings`,
      { address },
      {
        preserveScroll: true,
        onFinish: () => setSaving(false),
        onError: () => setSaving(false),
      },
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Agrega la dirección de tu negocio. Tus clientes la verán al agendar
        una cita.
      </p>

      {isComplete && current_business?.address ? (
        <div className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2 text-sm">
          <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
          <span className="flex-1">{current_business.address}</span>
          <a
            href={current_business.address.trim().match(/^https?:\/\//) ? current_business.address.trim() : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(current_business.address.trim())}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline text-xs"
          >
            Ver en mapa →
          </a>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="onb-address">Link de Google Maps o dirección</Label>
          <Input
            id="onb-address"
            placeholder="Ej: Av. Reforma 222, Col. Juárez, CDMX"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            required
          />
        </div>
        <Button type="submit" disabled={saving || !address.trim()} size="sm">
          {saving ? "Guardando..." : isComplete ? "Actualizar dirección" : "Guardar dirección"}
        </Button>
      </form>
    </div>
  );
}

// --- Services Step ---

function ServicesStep({
  services,
  orgSlug,
  isComplete,
}: {
  services: OnboardingData["services"];
  orgSlug: string;
  isComplete: boolean;
}) {
  const [name, setName] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const priceValue = parseFloat(price) || 0;
    router.post(
      `/${orgSlug}/services`,
      {
        service: {
          name,
          duration_minutes: durationMinutes,
          price_cents: Math.round(priceValue * 100),
          active: true,
        },
      },
      {
        preserveScroll: true,
        onFinish: () => {
          setSaving(false);
          setName("");
          setPrice("");
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Agrega los servicios que ofreces. Tu agente de IA los usará para
        responder preguntas de precios y agendar citas.
      </p>

      {/* Existing services */}
      {services.length > 0 && (
        <div className="space-y-2">
          {services.map((s) => (
            <div
              key={s.id}
              className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2 text-sm"
            >
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="flex-1">
                {s.emoji && `${s.emoji} `}
                {s.name}
              </span>
              <span className="text-muted-foreground">
                {s.duration_minutes}min
              </span>
              <span className="text-muted-foreground">
                ${(s.price_cents / 100).toLocaleString("es-MX")}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Add service form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="onb-service-name">Nombre del servicio</Label>
          <Input
            id="onb-service-name"
            placeholder="Ej: Corte de cabello"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="grid gap-3 grid-cols-2">
          <div className="space-y-2">
            <Label>Duración</Label>
            <Select
              value={durationMinutes.toString()}
              onValueChange={(v) => setDurationMinutes(parseInt(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value.toString()}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="onb-service-price">Precio (MXN)</Label>
            <Input
              id="onb-service-price"
              type="text"
              inputMode="decimal"
              placeholder="300"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />
          </div>
        </div>
        <Button type="submit" disabled={saving} size="sm">
          <Plus className="mr-1 h-4 w-4" />
          {saving ? "Guardando..." : "Agregar servicio"}
        </Button>
      </form>

      {services.length > 0 && !isComplete && (
        <p className="text-xs text-green-600">
          ✓ Ya tienes {services.length} servicio
          {services.length > 1 ? "s" : ""}. Puedes agregar más o continuar al
          siguiente paso.
        </p>
      )}
    </div>
  );
}

// --- Staff Step ---

function StaffStep({
  staff,
  orgSlug,
  isComplete,
}: {
  staff: OnboardingData["staff"];
  orgSlug: string;
  isComplete: boolean;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Agrega a las personas que atienden citas. Tu agente asignará las citas a
        quien esté disponible.
      </p>

      {/* Existing staff */}
      {staff.length > 0 && (
        <div className="space-y-2">
          {staff.map((s) => (
            <div
              key={s.id}
              className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2 text-sm"
            >
              <div
                className="h-6 w-6 flex-shrink-0 rounded-full"
                style={{ backgroundColor: s.color || "#6366f1" }}
              />
              <span className="flex-1">{s.name}</span>
              {s.pending && (
                <span className="text-xs text-muted-foreground">Pendiente</span>
              )}
            </div>
          ))}
        </div>
      )}

      <Button onClick={() => router.visit(`/${orgSlug}/team/new`)} size="sm">
        <Plus className="mr-1 h-4 w-4" />
        Agregar miembro del equipo
      </Button>

      {staff.length > 0 && !isComplete && (
        <p className="text-xs text-green-600">
          ✓ Ya tienes {staff.length} miembro{staff.length > 1 ? "s" : ""} en tu
          equipo.
        </p>
      )}
    </div>
  );
}

// --- Hours Step ---

function HoursStep({
  availability: initialAvailability,
  orgSlug,
}: {
  availability: AvailabilityRule[];
  orgSlug: string;
  isComplete: boolean;
}) {
  const [availability, setAvailability] = useState(initialAvailability);
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    router.put(
      `/${orgSlug}/settings/availability`,
      { availability },
      {
        preserveScroll: true,
        onFinish: () => setSaving(false),
      },
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Configura cuándo estás abierto. Tu agente solo ofrecerá citas en estos
        horarios.
      </p>

      <div className="rounded-lg border border-border">
        <HoursDayList
          availability={availability}
          onChange={setAvailability}
          compact
        />
      </div>

      <Button onClick={handleSave} disabled={saving} size="sm">
        {saving ? "Guardando..." : "Guardar horarios"}
      </Button>
    </div>
  );
}

// --- Demo Conversation Step ---

function DemoConversationStep({
  preview,
  completedSteps,
  orgSlug,
  isComplete,
}: {
  preview: ChatbotPreviewData;
  completedSteps: string[];
  orgSlug: string;
  isComplete: boolean;
}) {
  const { current_business } = usePage<PageProps>().props;
  const prerequisitesMet = DEMO_PREREQUISITES.every((step) =>
    completedSteps.includes(step),
  );
  const missingSteps = DEMO_PREREQUISITES.filter(
    (step) => !completedSteps.includes(step),
  );
  if (!prerequisitesMet) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Para probar tu agente con información real, primero agrega al menos un servicio.
          Tippy usará tus servicios, precios y duración para responder y agendar.
        </p>
        <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm">
          <p className="font-medium text-foreground">Falta completar:</p>
          <ul className="mt-1 list-disc list-inside text-muted-foreground">
            {missingSteps.map((step) => {
              const label = STEPS.find((s) => s.id === step)?.title || step;
              return <li key={step}>{label}</li>;
            })}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isComplete ? (
        <div className="flex items-center gap-3 rounded-lg bg-green-500/10 p-3 text-sm text-green-700">
          <PartyPopper className="h-5 w-5 shrink-0" />
          <span>¡Tu agente respondió! Puedes seguir probándolo o continuar con WhatsApp.</span>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Escribe como si fueras un cliente. El paso se completa cuando tu agente responda por primera vez.
        </p>
      )}

      <ChatbotPreviewPanel
        business={current_business ? { id: current_business.id, name: current_business.name } : null}
        messages={preview.messages}
        limits={preview.limits}
        messagePath={`/${orgSlug}/chatbot_preview/messages`}
        resetPath={`/${orgSlug}/chatbot_preview`}
        surface="onboarding"
        compact
      />
    </div>
  );
}

// --- WhatsApp Step ---

function WhatsAppStep({
  kapsoWhatsapp,
  orgSlug,
  hasOutbound,
}: {
  kapsoWhatsapp: OnboardingData["kapso_whatsapp"];
  billing: Billing | null;
  orgSlug: string;
  isComplete: boolean;
  hasOutbound: boolean;
}) {
  const [connecting, setConnecting] = useState(false);
  const isConnected = kapsoWhatsapp?.status === "connected";

  const handleConnect = () => {
    setConnecting(true);
    router.post(
      `/${orgSlug}/integrations/kapso/whatsapp/setup_link`,
      undefined,
      { onFinish: () => setConnecting(false) },
    );
  };

  if (isConnected) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-lg bg-green-500/10 px-4 py-3">
          <Check className="h-5 w-5 text-green-500" />
          <div>
            <p className="text-sm font-medium text-green-600">
              WhatsApp conectado
            </p>
            {kapsoWhatsapp?.display_address && (
              <p className="text-sm text-green-600/80">
                {kapsoWhatsapp.display_address}
              </p>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
          <div>
            <p className="text-sm font-medium text-foreground">
              Tu agente ya puede responder mensajes y ayudarte a agendar citas automáticamente.
            </p>
            {hasOutbound ? (
              <p className="text-sm text-muted-foreground mt-1">
                Ya detectamos una respuesta enviada desde tu número.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">
                Para probarlo, envía un WhatsApp a tu número con: “Hola, ¿qué servicios tienen?”
              </p>
            )}
          </div>

          <div className="rounded-md bg-background/70 px-3 py-2 text-sm">
            <p className="font-medium text-foreground">Recomendado antes de compartir tu número:</p>
            <ul className="mt-1 list-disc list-inside text-muted-foreground space-y-0.5">
              <li>Agrega la dirección de tu negocio</li>
              <li>Invita a tu equipo si más personas atienden citas</li>
              <li>Revisa tus horarios si no usas lunes a viernes de 9:00 a 17:00</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Conecta tu WhatsApp Business para que tu agente de IA pueda responder
        mensajes y agendar citas automáticamente. Seguirás pudiendo usar
        WhatsApp desde tu celular.
      </p>

      <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm space-y-1">
        <p className="font-medium text-foreground">Para conectar necesitas:</p>
        <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
          <li>
            Una cuenta de Facebook Business (Meta) — es gratis y solo toma 2 minutos crearla.{" "}
            <a
              href="https://business.facebook.com/overview"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Crear cuenta →
            </a>
          </li>
          <li>Un número de teléfono para WhatsApp Business (puede ser tu número actual de WhatsApp Business)</li>
          <li>No necesitas tarjeta ni método de pago para activar tu prueba gratis</li>
        </ul>
        <p className="text-muted-foreground">
          El proceso toma ~5 minutos. Te guiaremos paso a paso.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Button onClick={handleConnect} disabled={connecting} className="w-full">
          <Smartphone className="mr-2 h-4 w-4" />
          {connecting
            ? "Conectando..."
            : "Activar prueba gratis y conectar WhatsApp"}
        </Button>
        <button
          type="button"
          onClick={() => {
            router.post(`/${orgSlug}/onboarding/dismiss`, undefined, {
              preserveScroll: true,
            });
          }}
          className="text-sm text-muted-foreground hover:text-foreground text-center"
        >
          Ahora no
        </button>
      </div>
    </div>
  );
}

// --- Test Message Step ---

function TestMessageStep({
  kapsoWhatsapp,
  hasOutbound,
  orgSlug,
  isComplete,
}: {
  kapsoWhatsapp: OnboardingData["kapso_whatsapp"];
  hasOutbound: boolean;
  orgSlug: string;
  isComplete: boolean;
}) {
  const [detected, setDetected] = useState(hasOutbound || isComplete);
  const [copied, setCopied] = useState(false);
  const isConnected = kapsoWhatsapp?.status === "connected";
  const phoneNumber = kapsoWhatsapp?.display_address;

  // Poll for outbound messages
  useEffect(() => {
    if (detected || !isConnected) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/${orgSlug}/onboarding/test_message_status`, {
          headers: { Accept: "application/json" },
        });
        const data = await res.json();
        if (data.has_outbound) {
          setDetected(true);
        }
      } catch {
        // ignore
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [detected, isConnected, orgSlug]);

  const handleCopy = useCallback(() => {
    if (!phoneNumber) return;
    try {
      navigator.clipboard.writeText(phoneNumber.replace(/\D/g, ""));
    } catch {
      // Clipboard API unavailable (non-HTTPS / local dev)
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [phoneNumber]);

  if (!isConnected) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Primero conecta tu WhatsApp en el paso anterior.
        </p>
      </div>
    );
  }

  if (detected) {
    return (
      <div className="space-y-4 text-center py-4">
        <PartyPopper className="h-12 w-12 text-primary mx-auto" />
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            ¡Tu agente está funcionando! 🎉
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Ya está listo para responder a tus clientes por WhatsApp.
          </p>
        </div>
        <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm">
          <p className="text-muted-foreground">
            Comparte este número con tus clientes:
          </p>
          <p className="text-lg font-semibold text-foreground mt-1">
            {phoneNumber}
          </p>
          <div className="flex gap-2 justify-center mt-3">
            <Button size="sm" variant="outline" onClick={handleCopy}>
              <Clipboard className="mr-1 h-4 w-4" />
              {copied ? "¡Copiado!" : "Copiar número"}
            </Button>
            {phoneNumber && (
              <Button
                size="sm"
                onClick={() => {
                  const cleanNumber = phoneNumber.replace(/\D/g, "");
                  window.open(
                    `https://wa.me/${cleanNumber}?text=${encodeURIComponent("Hola, ¿qué servicios tienen?")}`,
                    "_blank",
                  );
                }}
              >
                <MessageCircle className="mr-1 h-4 w-4" />
                Probar
              </Button>
            )}
          </div>
        </div>
        <Button
          onClick={() => {
            router.post(
              `/${orgSlug}/onboarding/complete_step`,
              { step: "test_message" },
              { onSuccess: () => router.reload() },
            );
          }}
          className="mt-2"
        >
          🎉 ¡Listo, ya lo probé!
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        ¡Ya casi! Manda un mensaje de WhatsApp a tu número de negocio para ver a
        tu agente en acción.
      </p>

      <div className="rounded-lg border border-border bg-muted/30 p-4 text-center space-y-3">
        <p className="text-sm text-muted-foreground">Manda un WhatsApp al:</p>
        <p className="text-2xl font-semibold text-foreground">{phoneNumber}</p>
        <p className="text-sm text-muted-foreground">con el mensaje:</p>
        <p className="text-lg font-medium text-foreground italic">
          "Hola, ¿qué servicios tienen?"
        </p>
        <div className="flex gap-2 justify-center pt-2">
          <Button size="sm" variant="outline" onClick={handleCopy}>
            <Clipboard className="mr-1 h-4 w-4" />
            {copied ? "¡Copiado!" : "Copiar número"}
          </Button>
          {phoneNumber && (
            <Button
              size="sm"
              onClick={() => {
                const cleanNumber = phoneNumber.replace(/\D/g, "");
                window.open(
                  `https://wa.me/${cleanNumber}?text=${encodeURIComponent("Hola, ¿qué servicios tienen?")}`,
                  "_blank",
                );
              }}
            >
              <MessageCircle className="mr-1 h-4 w-4" />
              Abrir WhatsApp
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="h-2 w-2 animate-pulse rounded-full bg-primary" />
          Esperando respuesta de tu agente...
        </div>
        <button
          type="button"
          onClick={() => {
            router.post(`/${orgSlug}/onboarding/dismiss`, undefined, {
              preserveScroll: true,
            });
          }}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Omitir
        </button>
      </div>
    </div>
  );
}
